/**
 * AYAQ Minimal Variant Grid JavaScript
 * Interactive behavior for color variant carousels, sticky filters, and model navigation.
 * Complies with Shopify JavaScript standards (Module pattern, private methods `#`).
 */

class AyaqVariantCarousel {
  #container;
  #carouselTrack;
  #slides;
  #dots;
  #prevBtn;
  #nextBtn;
  #currentIndex = 0;
  #hasUserNavigated = false;
  #isTouchPreviewing = false;
  #touchStartX = 0;
  #touchEndX = 0;

  constructor(containerElement) {
    if (!containerElement) return;

    this.#container = containerElement;
    this.#carouselTrack = this.#container.querySelector('.ayaq-card-variant__track') || this.#container.querySelector('.ayaq-card-variant__carousel');
    this.#slides = Array.from(this.#container.querySelectorAll('.ayaq-card-variant__slide'));
    this.#dots = Array.from(this.#container.querySelectorAll('.ayaq-card-variant__dot'));
    this.#prevBtn = this.#container.querySelector('.ayaq-card-variant__nav-btn--prev');
    this.#nextBtn = this.#container.querySelector('.ayaq-card-variant__nav-btn--next');

    if (this.#slides.length <= 1) {
      if (this.#prevBtn) this.#prevBtn.style.display = 'none';
      if (this.#nextBtn) this.#nextBtn.style.display = 'none';
      const dotsContainer = this.#container.querySelector('.ayaq-card-variant__dots');
      if (dotsContainer) dotsContainer.style.display = 'none';
      return;
    }

    this.#initEvents();
  }

  setSlide(index) {
    if (this.#slides.length <= 1) return;
    const targetIdx = index < this.#slides.length ? index : 0;
    this.#hasUserNavigated = false;
    this.#goToSlide(targetIdx);
  }

  resetToViewMode() {
    this.#hasUserNavigated = false;
    if (this.#carouselTrack) {
      this.#carouselTrack.classList.remove('ayaq-card-variant__carousel--user-navigated');
    }
    const targetSlide = (window.ayaqActiveViewMode || 'model') === 'product' ? 1 : 0;
    this.#goToSlide(targetSlide);
  }

  #isHoverSlideEnabled() {
    const gridWrapper = this.#container.closest('.ayaq-minimal-grid-wrapper');
    if (!gridWrapper) return true;
    return gridWrapper.dataset.hoverSlideEnabled !== 'false';
  }

  #initEvents() {
    // Hover events: always preview opposite view on hover & reset to active view mode on mouseleave
    this.#container.addEventListener('mouseenter', () => {
      if (!this.#isHoverSlideEnabled()) return;
      if (this.#slides.length <= 1) return;

      // If the user has manually navigated, don't override their slide choice on hover
      if (this.#hasUserNavigated) return;

      const currentMode = window.ayaqActiveViewMode || 'model';
      if (currentMode === 'model') {
        this.#goToSlide(1);
      } else if (currentMode === 'product') {
        this.#goToSlide(0);
      }
    });

    this.#container.addEventListener('mouseleave', () => {
      if (!this.#isHoverSlideEnabled()) return;
      if (this.#slides.length <= 1) return;

      // If the user has manually navigated via buttons, keep their chosen slide
      if (this.#hasUserNavigated) return;

      const currentMode = window.ayaqActiveViewMode || 'model';
      if (currentMode === 'model') {
        this.#goToSlide(0);
      } else if (currentMode === 'product') {
        this.#goToSlide(1);
      }
    });

    // Reset to view-mode default when the card scrolls out of the viewport
    const resetObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && this.#hasUserNavigated) {
          this.resetToViewMode();
        }
      },
      { threshold: 0 }
    );
    resetObserver.observe(this.#container);

    if (this.#prevBtn) {
      this.#prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#markNavigated();
        this.#handlePrevClick();
      });
    }

    if (this.#nextBtn) {
      this.#nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#markNavigated();
        this.#handleNextClick();
      });
    }

    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.#markNavigated();
        this.#goToSlide(index);
      });
    });

    // Touch events — hover preview on tap, swipe navigation
    this.#container.addEventListener('touchstart', (e) => {
      if (e.target.closest('.ayaq-card-variant__nav-btn') || e.target.closest('.ayaq-card-variant__dot')) {
        return;
      }
      this.#touchStartX = e.changedTouches[0].screenX;

      // Show hover preview (opposite slide) if the user hasn't manually navigated and hover is enabled
      if (this.#isHoverSlideEnabled() && !this.#hasUserNavigated && this.#slides.length > 1) {
        this.#isTouchPreviewing = true;
        const currentMode = window.ayaqActiveViewMode || 'model';
        if (currentMode === 'model') {
          this.#goToSlide(1);
        } else if (currentMode === 'product') {
          this.#goToSlide(0);
        }
      }
    }, { passive: true });

    this.#container.addEventListener('touchend', (e) => {
      if (e.target.closest('.ayaq-card-variant__nav-btn') || e.target.closest('.ayaq-card-variant__dot')) {
        return;
      }
      this.#touchEndX = e.changedTouches[0].screenX;
      const diff = Math.abs(this.#touchEndX - this.#touchStartX);

      if (diff > 35) {
        // Swipe: mark as navigated and handle the swipe direction
        this.#isTouchPreviewing = false;
        this.#markNavigated();
        this.#handleSwipe();
      } else if (this.#isTouchPreviewing) {
        // Tap (not a swipe): reset to view-mode default after a short delay
        // so the user can briefly see the preview before it snaps back
        this.#isTouchPreviewing = false;
        setTimeout(() => {
          if (!this.#hasUserNavigated) {
            const currentMode = window.ayaqActiveViewMode || 'model';
            if (currentMode === 'model') {
              this.#goToSlide(0);
            } else if (currentMode === 'product') {
              this.#goToSlide(1);
            }
          }
        }, 400);
      }
    }, { passive: true });
  }

  #markNavigated() {
    this.#hasUserNavigated = true;
    if (this.#carouselTrack) {
      this.#carouselTrack.classList.add('ayaq-card-variant__carousel--user-navigated');
    }
  }

  #handleNextClick() {
    const newIndex = (this.#currentIndex + 1) % this.#slides.length;
    this.#goToSlide(newIndex);
  }

  #handlePrevClick() {
    const newIndex = (this.#currentIndex - 1 + this.#slides.length) % this.#slides.length;
    this.#goToSlide(newIndex);
  }

  #updateDotHighlight(activeIdx) {
    this.#dots.forEach((dot, idx) => {
      if (idx === activeIdx) {
        dot.classList.add('ayaq-card-variant__dot--active');
      } else {
        dot.classList.remove('ayaq-card-variant__dot--active');
      }
    });
  }

  #goToSlide(index) {
    if (index < 0 || index >= this.#slides.length) return;

    if (this.#carouselTrack) {
      this.#carouselTrack.style.transform = `translateX(-${index * 100}%)`;
    }

    this.#slides.forEach((slide, idx) => {
      if (idx === index) {
        slide.classList.add('ayaq-card-variant__slide--active');
      } else {
        slide.classList.remove('ayaq-card-variant__slide--active');
      }
    });

    this.#currentIndex = index;
    this.#updateDotHighlight(index);
  }

  #handleSwipe() {
    const threshold = 35;
    const diff = this.#touchEndX - this.#touchStartX;

    if (Math.abs(diff) > threshold) {
      if (diff < 0) {
        this.#handleNextClick();
      } else {
        this.#handlePrevClick();
      }
    }
  }
}

const HEADER_ROOT_SELECTORS = ['sticky-header', '.header-wrapper', '#shopify-section-header'];

// Every layer the header stack can paint. They are stacked, not nested, and some
// of them are taken out of the flow, so each one has to be measured on its own.
const HEADER_LAYER_SELECTORS = [
  ...HEADER_ROOT_SELECTORS,
  '#top-universes',
  '#site-header',
  '.affiliate-banner'
];

// A rect is only usable as a viewport offset when the element is carried by a
// fixed ancestor. Anything else scrolls away and must not be offset for.
const isViewportPinned = (element) => {
  for (let node = element; node && node !== document.body; node = node.parentElement) {
    if (window.getComputedStyle(node).position === 'fixed') return true;
  }

  return false;
};

const getHeaderRoot = () => {
  for (const selector of HEADER_ROOT_SELECTORS) {
    const root = document.querySelector(selector);

    if (root) return root;
  }

  return null;
};

// Lowest edge the header stack occupies on screen, affiliate banner included.
const getPinnedHeaderBottom = () => {
  let bottom = 0;

  for (const selector of HEADER_LAYER_SELECTORS) {
    const element = document.querySelector(selector);

    if (!element) continue;

    const rect = element.getBoundingClientRect();

    if (rect.height === 0 || !isViewportPinned(element)) continue;

    bottom = Math.max(bottom, rect.bottom);
  }

  return Math.max(0, Math.round(bottom));
};

class AyaqMinimalGridManager {
  #wrapper;
  #carousels = [];

  constructor() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init());
    } else {
      this.#init();
    }
  }

  #init() {
    this.#wrapper = document.querySelector('.ayaq-minimal-grid-wrapper') || document.body;

    // Load saved view mode (default: 'model')
    window.ayaqActiveViewMode = localStorage.getItem('ayaq_view_mode') || 'model';

    this.#initCarousels();
    this.#initViewToggle();
    this.#initSmoothScrolling();
    this.#initStickyFilters();
    this.#observeGridChanges();

    const previousSetup = window.setUpPageWhenLoaded;

    window.setUpPageWhenLoaded = () => {
      if (typeof previousSetup === 'function') {
        previousSetup();
      }

      this.#initCarousels();
      this.#applyActiveViewMode();
      this.#initSmoothScrolling();
    };
  }

  #initStickyFilters() {
    const stickyBar = document.querySelector('.ayaq-sticky-filters');
    if (!stickyBar) return;

    let sentinel = stickyBar.previousElementSibling;

    if (!sentinel || !sentinel.classList.contains('ayaq-sticky-filters__sentinel')) {
      sentinel = document.createElement('div');
      sentinel.className = 'ayaq-sticky-filters__sentinel';
      stickyBar.parentNode.insertBefore(sentinel, stickyBar);
    }

    let stuckObserver = null;
    let appliedOffset = null;

    const syncStickyOffset = () => {
      const offset = getPinnedHeaderBottom();

      if (offset === appliedOffset) return;

      appliedOffset = offset;
      document.documentElement.style.setProperty('--ayaq-sticky-header-height', `${offset}px`);

      if (stuckObserver) {
        stuckObserver.disconnect();
      }

      stuckObserver = new IntersectionObserver(
        ([entry]) => stickyBar.classList.toggle('is-stuck', !entry.isIntersecting),
        { rootMargin: `-${offset}px 0px 0px 0px`, threshold: 0 }
      );
      stuckObserver.observe(sentinel);
    };

    syncStickyOffset();
    window.addEventListener('resize', syncStickyOffset, { passive: true });
    window.addEventListener('load', syncStickyOffset);

    const headerRoot = getHeaderRoot();

    if (headerRoot) {
      // The affiliate banner, universes bar and search bar are all revealed
      // after load, each time changing where the header stack ends
      new ResizeObserver(syncStickyOffset).observe(headerRoot);

      // Dawn also toggles the header between static and sticky while scrolling
      new MutationObserver(syncStickyOffset).observe(headerRoot, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
  }

  #initCarousels() {
    const cardMediaContainers = document.querySelectorAll('.ayaq-card-variant__media-container');
    cardMediaContainers.forEach((container) => {
      if (container.dataset.ayaqCarouselInitialized === 'true') return;
      container.dataset.ayaqCarouselInitialized = 'true';
      const instance = new AyaqVariantCarousel(container);
      container.ayaqCarouselInstance = instance;
      this.#carousels.push(instance);
    });
    this.#applyActiveViewMode();
  }

  #initViewToggle() {
    const viewButtons = document.querySelectorAll('.js-ayaq-view-btn');
    const toggleTexts = document.querySelectorAll('.js-ayaq-view-toggle-text');
    const toggleDetails = document.querySelectorAll('.js-ayaq-view-toggle');

    // Update UI text initially
    const initialText = window.ayaqActiveViewMode === 'product' ? 'Vue produit' : 'Vue modèle';
    toggleTexts.forEach((elem) => {
      elem.textContent = initialText;
    });

    viewButtons.forEach((btn) => {
      if (btn.dataset.ayaqViewBtnInitialized === 'true') return;
      btn.dataset.ayaqViewBtnInitialized = 'true';

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }

        const viewMode = btn.dataset.view; // 'model' or 'product'
        window.ayaqActiveViewMode = viewMode;
        localStorage.setItem('ayaq_view_mode', viewMode);

        const newText = viewMode === 'product' ? 'Vue produit' : 'Vue modèle';
        toggleTexts.forEach((elem) => {
          elem.textContent = newText;
        });

        // Close dropdown
        toggleDetails.forEach((dt) => dt.removeAttribute('open'));

        // Apply slide change across all cards
        this.#applyActiveViewMode();
      });
    });
  }

  #applyActiveViewMode() {
    const targetSlide = window.ayaqActiveViewMode === 'product' ? 1 : 0;
    const cardMediaContainers = document.querySelectorAll('.ayaq-card-variant__media-container');
    cardMediaContainers.forEach((container) => {
      if (container.ayaqCarouselInstance) {
        container.ayaqCarouselInstance.setSlide(targetSlide);
      }
    });
  }

  #initSmoothScrolling() {
    const modelLinks = document.querySelectorAll('.ayaq-models-overview__card');
    modelLinks.forEach((link) => {
      if (link.dataset.ayaqScrollInitialized === 'true') return;
      link.dataset.ayaqScrollInitialized = 'true';

      link.addEventListener('click', (e) => {
        const targetId = link.getAttribute('href');
        if (targetId && targetId.startsWith('#')) {
          const targetElem = document.querySelector(targetId);
          if (targetElem) {
            e.preventDefault();
            const stickyBar = document.querySelector('.ayaq-sticky-filters');
            const stickyBarHeight = stickyBar ? stickyBar.getBoundingClientRect().height : 0;
            const stickyOffset = getPinnedHeaderBottom() + stickyBarHeight + 16;
            const elementPosition = targetElem.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.scrollY - stickyOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }
      });
    });
  }

  #observeGridChanges() {
    const wrapper = document.getElementById('ProductGridContainer') || document.querySelector('.ayaq-minimal-grid-wrapper');
    if (!wrapper) return;

    let isProcessing = false;
    const observer = new MutationObserver((mutations) => {
      if (isProcessing) return;

      const hasAddedNodes = mutations.some((m) => m.addedNodes && m.addedNodes.length > 0);
      if (!hasAddedNodes) return;

      isProcessing = true;
      this.#initCarousels();
      this.#initViewToggle();
      this.#initSmoothScrolling();
      setTimeout(() => {
        isProcessing = false;
      }, 100);
    });

    observer.observe(wrapper, { childList: true, subtree: false });
  }
}

// Initialize manager instance
new AyaqMinimalGridManager();

// Re-initialize in Shopify Theme Editor when section settings are modified
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.ayaq-minimal-grid-wrapper') || event.target.classList.contains('ayaq-minimal-grid-wrapper')) {
    new AyaqMinimalGridManager();
  }
});
